import {createTheme}        from '@mui/material/styles';
import type {ReferralBrand} from 'components/Referral/brand';

/**
 * Per-brand palette for the referral journey's own theme. Energy values are the pre-CD-8652
 * literals and must not change — the energy variant has to stay pixel-identical.
 *
 * The communications `main` is a PLACEHOLDER pending design sign-off (CD-8652). It is the app's
 * existing --color-comms from assets/styles/modules/_base.scss, not a signed-off referral token.
 *
 * `light` mirrors `main` for both brands. The referral journey does not use the main app's
 * light-tint concept: energy sets light to its own main rather than --color-energy-light
 * (#f1425e), so comms follows suit rather than importing --color-comms-light (#46a8d4). That
 * tint is decorative in the main app's CSS-variable system and only reaches 2.69:1 on white,
 * under the 3:1 floor WCAG 2.1 AA sets for UI components and large text, which AC 13 requires
 * the communications variant to meet in its own right. If design supplies a real referral light
 * token, it belongs here and must clear 3:1.
 */
const brandPalettes: Record<ReferralBrand, {main: string, light: string}> = {
  energy:         {main: '#DC2040', light: '#DC2040'},
  communications: {main: '#2174AB', light: '#2174AB'},
}

export const createReferralTheme = (brand: ReferralBrand = 'energy') => createTheme({
  palette: {
    primary: {
      main: brandPalettes[brand].main,
      light: brandPalettes[brand].light
    },
    warning: {
      main: '#ed6c02'
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 670,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: 'Poppins, sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2rem'
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.5rem'
    },
    h3: {
      fontWeight: 700,
      fontSize: '1.25rem'
    },
    h4: {
      fontWeight: 700,
      fontSize: '1rem'
    },
    h5: {
      fontWeight: 700,
      fontSize: '0.9em'
    },
    h6: {
      fontWeight: 700,
      fontSize: '0.8rem'
    }
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#000',
        },
        gutterBottom: {
          marginBottom: '0.5em'
        },
        body2: {
          opacity: 0.7,
          fontWeight: 400,
          fontSize: '1rem'
        },
        subtitle1: {
          lineHeight: 1.5,
        },
        caption: {
          display: 'block',
        }
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        fullWidth: true,
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.85rem',
          marginTop: '0.3rem',
          marginLeft: 0,
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          padding: '0.5rem 1.5rem',
        },
        contained: {
          '&:hover, &:focus': {
            color: '#fff',
          },
        },
      },
      defaultProps: {
        variant: 'contained',
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'inherit',
          textDecorationColor: 'inherit',
          '&:hover, &:focus': {
            // Brand-resolved, matching createGlobalStyles' plain-anchor rule. Hard-coding energy
            // here rendered energy red inside a comms journey.
            color: brandPalettes[brand].main,
          }
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#fafafa',
          borderColor: '#e4e4e7',
        },
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 32,
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        secondary: {
          opacity: 0.8,
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          padding: '0.5rem',
          '&.Mui-selected, &:hover': {
            backgroundColor: '#ededed',
          }
        },
      }
    },
    MuiAlert: {
      defaultProps: {
        variant: 'filled',
      },
      styleOverrides: {
        root: {
          textAlign: 'left',
        },
        action: {
          paddingTop: '2px',
        }
      }
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          color: 'inherit'
        }
      }
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: '#000',
          color: '#fff',
          fontSize: '0.875rem',
          borderRadius: '0.25rem',
          padding: '0.5rem 1rem',
          maxWidth: '20rem',
        },
        arrow: {
          color: '#000',
        }
      }
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          display: 'block',
          color: 'inherit',
          lineHeight: 'inherit'
        },
        asterisk: { color: 'red' }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#000',
          }
        }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#000',
          }
        }
      }
    }
  }
})

export const createGlobalStyles = (brand: ReferralBrand = 'energy') => ({
  a: {
    color: 'inherit',
    textDecoration: 'underline',
    '&:hover, &:focus': {
      color: brandPalettes[brand].main,
    }
  }
})

// Energy defaults, kept as the module's exports so the staff-UI consumers
// (AppNotifications, DownloadManagerWidget) are untouched by brand resolution.
export const globalStyles = createGlobalStyles()

export default createReferralTheme();
